import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, MonoTypeOperatorFunction, Observable, of, retry, throwError, timer } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { SKIP_HTTP_ERROR_LOG_FOR_404 } from '../../../../core/interceptors/http-error.interceptor';
import {
  Feedback,
  MissingTranscriptBody,
  PutFeedbackBody,
  Questionnaire,
} from './transcript-feedback.model';

const RETRY_DELAYS_MS = [500, 1000];
// 0 = the request never reached a server (network failure). Never 409: an outdated
// questionnaire stays outdated, the caller reloads the questions instead.
const RETRYABLE_STATUS = new Set([0, 502, 503, 504]);

/**
 * Both writes are safe to repeat (one row per call and person; a repeated
 * missing-transcript POST returns the existing row unchanged), so a temporary
 * failure is retried before the user sees an error.
 */
function retryTemporaryFailure<T>(): MonoTypeOperatorFunction<T> {
  return retry({
    count: RETRY_DELAYS_MS.length,
    delay: (err: unknown, retryCount: number) =>
      err instanceof HttpErrorResponse && RETRYABLE_STATUS.has(err.status)
        ? timer(RETRY_DELAYS_MS[retryCount - 1])
        : throwError(() => err),
  });
}

/**
 * Talks to aida-transcription-api transcript feedback endpoints.
 *
 *   Questionnaire      : GET  /transcription/v1/transcript-feedback-questionnaires/current
 *   My feedback        : GET  /transcription/v1/transcript-feedbacks/{cchsid}/me
 *   Save feedback      : PUT  /transcription/v1/transcript-feedbacks/{cchsid}/me
 *   Missing transcript : POST /transcription/v1/transcript-feedbacks/{cchsid}/me/missing-transcript
 *                        (201 created; 200 = the user already had feedback, the existing
 *                        row comes back unchanged and its `kind` may be `rating`)
 *
 * @ticket QAS-6936
 */
@Injectable({ providedIn: 'root' })
export class TranscriptFeedbackService {
  private readonly http = inject(HttpClient);

  findQuestionnaire(): Observable<Questionnaire> {
    return this.http.get<Questionnaire>(
      `${environment.transcriptionUrl}/transcript-feedback-questionnaires/current`,
    );
  }

  /** Emits `null` when the user has not given feedback for this call yet. */
  findMyFeedback(cchsid: string): Observable<Feedback | null> {
    return this.http
      .get<Feedback>(`${this.baseUrl()}/${encodeURIComponent(cchsid)}/me`, {
        // A 404 is the normal "no feedback yet" answer, not a fault to log.
        context: new HttpContext().set(SKIP_HTTP_ERROR_LOG_FOR_404, true),
      })
      .pipe(
        catchError((err: unknown) =>
          err instanceof HttpErrorResponse && err.status === 404
            ? of(null)
            : throwError(() => err),
        ),
      );
  }

  putMyFeedback(cchsid: string, body: PutFeedbackBody): Observable<Feedback> {
    return this.http
      .put<Feedback>(`${this.baseUrl()}/${encodeURIComponent(cchsid)}/me`, body)
      .pipe(retryTemporaryFailure());
  }

  /**
   * Emits the stored row. Read its `kind`: when the user had already rated the
   * call, aida returns that `rating` row unchanged instead of a new report.
   */
  reportMissing(cchsid: string): Observable<Feedback> {
    const body: MissingTranscriptBody = { sourceApp: 'amie' };
    return this.http
      .post<Feedback>(`${this.baseUrl()}/${encodeURIComponent(cchsid)}/me/missing-transcript`, body)
      .pipe(retryTemporaryFailure());
  }

  private baseUrl(): string {
    return `${environment.transcriptionUrl}/transcript-feedbacks`;
  }
}
